export const BARON_BUFF_SPEC = {
  id: 'baron_buff',
  name: 'Exalted with Baron Nashor',
  duration: 240,
  ad: 40,
  ap: 40,
  hpRegenPercent5s: 0.03,
  mpRegenPercent5s: 0.01
};

export function applyBaronBuff(entityOrStats, duration = 240) {
  if (!entityOrStats) return null;
  
  if (!entityOrStats.buffs && (entityOrStats.ad !== undefined || entityOrStats.ap !== undefined)) {
    return {
      ...entityOrStats,
      ad: (entityOrStats.ad || 0) + 40,
      ap: (entityOrStats.ap || 0) + 40
    };
  }

  if (!entityOrStats.buffs) entityOrStats.buffs = [];

  const existing = entityOrStats.buffs.find(b => b.id === 'baron_buff');
  if (existing) {
    existing.duration = duration;
    return existing;
  }
  const buff = {
    id: 'baron_buff',
    name: BARON_BUFF_SPEC.name,
    duration,
    ad: BARON_BUFF_SPEC.ad,
    ap: BARON_BUFF_SPEC.ap
  };
  entityOrStats.buffs.push(buff);
  return buff;
}

export function processMajorObjectiveKill(campState, killerEntity, teamMembers = [], gameTimeMinutes = 0) {
  if (!campState || !campState.isAlive) {
    return { success: false, reason: 'CAMP_ALREADY_DEAD' };
  }

  campState.isAlive = false;
  campState.lastKilledTime = gameTimeMinutes * 60;

  if (campState.monsters) {
    campState.monsters.forEach(m => {
      m.isDead = true;
      m.hp = 0;
    });
  }

  const campId = campState.id;
  const isDragon = campId === 'dragon';
  const isBaron = campId === 'baron';

  const killerTeam = killerEntity ? killerEntity.team : 'blue';
  const allies = teamMembers.filter(member => member && member.team === killerTeam);

  const globalGold = isDragon ? 190 : (isBaron ? 300 : 100);
  const globalXp = isDragon ? 400 : (isBaron ? 900 : 200);

  allies.forEach(ally => {
    ally.gold = (ally.gold || 0) + globalGold;
    ally.xp = (ally.xp || 0) + globalXp;
  });

  // Killer bonus gold
  if (killerEntity && isDragon) {
    killerEntity.gold = (killerEntity.gold || 0) + 25;
  }

  // Grant Baron Buff to all alive allies
  if (isBaron) {
    allies.forEach(ally => {
      if (!ally.isDead && (ally.hp === undefined || ally.hp > 0)) {
        applyBaronBuff(ally, 240);
      }
    });
  }

  return {
    success: true,
    objective: campId,
    globalGoldGranted: globalGold,
    globalXpGranted: globalXp
  };
}


export const BLUE_BUFF_SPEC = {
  id: 'blue_buff',
  name: 'Crest of Ancient Insight',
  duration: 150,
  cdr: 0.20,
  mpRegenPercent: 0.005
};

export const RED_BUFF_SPEC = {
  id: 'red_buff',
  name: 'Blessing of Lizard Elder',
  duration: 150,
  slowMelee: 0.15,
  slowRanged: 0.08
};

export function applyBlueBuff(entity, duration = 150) {
  if (!entity) return null;
  if (!entity.buffs) entity.buffs = [];
  
  const existing = entity.buffs.find(b => b.id === 'blue_buff');
  if (existing) {
    existing.duration = duration;
    return existing;
  }
  const buff = {
    id: 'blue_buff',
    name: BLUE_BUFF_SPEC.name,
    duration,
    cdr: BLUE_BUFF_SPEC.cdr
  };
  entity.buffs.push(buff);
  return buff;
}

export function applyRedBuff(entity, duration = 150) {
  if (!entity) return null;
  if (!entity.buffs) entity.buffs = [];
  
  const existing = entity.buffs.find(b => b.id === 'red_buff');
  if (existing) {
    existing.duration = duration;
    return existing;
  }
  const buff = {
    id: 'red_buff',
    name: RED_BUFF_SPEC.name,
    duration
  };
  entity.buffs.push(buff);
  return buff;
}

export function onHitRedBuff(attacker, target) {
  if (!attacker || !target || !attacker.buffs) return { applied: false };
  const redBuff = attacker.buffs.find(b => b.id === 'red_buff');
  if (!redBuff) return { applied: false };

  const level = attacker.level || 1;
  const burnDamage = 10 + (level * 2);
  if (typeof target.takeDamage === 'function') {
    target.takeDamage(burnDamage, 'true');
  }

  const slowPercent = attacker.isRanged ? RED_BUFF_SPEC.slowRanged : RED_BUFF_SPEC.slowMelee;
  if (target.ms) {
    target.ms = target.ms * (1 - slowPercent);
  }

  return { applied: true, burnDamage, slowPercent };
}

export function updateBuffs(entity, dt = 1.0) {
  if (!entity || !entity.buffs || entity.buffs.length === 0) return;

  const blueBuff = entity.buffs.find(b => b.id === 'blue_buff');
  if (blueBuff && entity.mp !== undefined && entity.maxMp !== undefined) {
    const regen = (entity.maxMp * BLUE_BUFF_SPEC.mpRegenPercent + (entity.mpRegen || 5)) * dt;
    entity.mp = Math.min(entity.maxMp, entity.mp + regen);
  }

  const baronBuff = entity.buffs.find(b => b.id === 'baron_buff');
  if (baronBuff) {
    if (entity.hp !== undefined && entity.maxHp !== undefined) {
      const hpRegen = (entity.maxHp * (BARON_BUFF_SPEC.hpRegenPercent5s / 5) + (entity.hpRegen || 5)) * dt;
      entity.hp = Math.min(entity.maxHp, entity.hp + hpRegen);
    }
    if (entity.mp !== undefined && entity.maxMp !== undefined) {
      const mpRegen = (entity.maxMp * (BARON_BUFF_SPEC.mpRegenPercent5s / 5) + (entity.mpRegen || 5)) * dt;
      entity.mp = Math.min(entity.maxMp, entity.mp + mpRegen);
    }
  }

  for (let i = entity.buffs.length - 1; i >= 0; i--) {
    entity.buffs[i].duration -= dt;
    if (entity.buffs[i].duration <= 0) {
      entity.buffs.splice(i, 1);
    }
  }
}

export function transferBuffsOnKill(victim, killer) {
  if (!victim || !victim.buffs || !killer) return;
  if (!killer.buffs) killer.buffs = [];

  const blue = victim.buffs.find(b => b.id === 'blue_buff');
  if (blue) {
    applyBlueBuff(killer, 150);
  }

  const red = victim.buffs.find(b => b.id === 'red_buff');
  if (red) {
    applyRedBuff(killer, 150);
  }
}
